import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { EmptyState, IconButton, InlineError, Skeleton } from '../../../components/ui'
import { setSlot } from '../../../features/booking/selectionSlice'
import { useGetAvailabilityQuery, useGetAvailableDatesQuery } from '../../../services/bookingApi'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { useTenant } from '../../../tenant/useTenant'
import { cn } from '../../../utils/cn'
import {
  addDaysKey,
  BAND_ICON,
  BAND_LABEL,
  bandOf,
  dayOfMonth,
  formatLongDate,
  formatSlotTime,
  monthKey,
  monthShort,
  slotDate,
  todayInZone,
  weekdayShort,
  type SlotBand,
} from '../../../utils/datetime'
import { useWizardAction } from '../wizardContext'
import type { Slot } from '../../../types/storefront'

const STRIP_DAYS = 14
const BANDS: SlotBand[] = ['morning', 'afternoon', 'evening']

/**
 * G5 — every date and time on this screen is the salon's, taken from
 * `bootstrap.timezone` and from the offsets the slots arrive with. The browser
 * timezone is never consulted, so a customer booking from abroad picks the same
 * 10:00 the salon has on its wall.
 */
export function TimeStep() {
  const { slug, bootstrap } = useTenant()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const selection = useAppSelector((s) => s.selection)

  const today = useMemo(() => todayInZone(bootstrap.timezone), [bootstrap.timezone])
  const [stripStart, setStripStart] = useState(() => {
    const chosen = selection.slotStart ? slotDate(selection.slotStart) : ''
    return chosen && chosen > today ? chosen : today
  })
  const [date, setDate] = useState(() =>
    selection.slotStart ? slotDate(selection.slotStart) || today : today,
  )

  const branchId = selection.branchId
  const serviceIds = useMemo(() => selection.services.map((s) => s.id), [selection.services])
  // Parallel to serviceIds; 0 stands for "any staff" so the array never has holes.
  const staffIds = useMemo(() => selection.services.map((s) => s.staffId ?? 0), [selection.services])
  const hasPreference = staffIds.some((id) => id !== 0)
  // Only meaningful when nobody is named and there is more than one service.
  const sameStaff = selection.sameStaff && !hasPreference && serviceIds.length > 1

  const strip = useMemo(
    () => Array.from({ length: STRIP_DAYS }, (_, i) => addDaysKey(stripStart, i)),
    [stripStart],
  )

  const months = useMemo(() => {
    const set = new Set(strip.map(monthKey))
    return [...set]
  }, [strip])

  const skipQueries = branchId === null || serviceIds.length === 0

  const firstMonth = useGetAvailableDatesQuery(
    { slug, branchId: branchId ?? 0, month: months[0] ?? '', serviceIds, sameStaff },
    { skip: skipQueries || !months[0] },
  )
  const secondMonth = useGetAvailableDatesQuery(
    { slug, branchId: branchId ?? 0, month: months[1] ?? '', serviceIds, sameStaff },
    { skip: skipQueries || !months[1] },
  )

  const openDates = useMemo(() => {
    const all = [...(firstMonth.data ?? []), ...(secondMonth.data ?? [])]
    // No answer yet is not the same as "closed": until the months load, every
    // day stays tappable rather than flashing as unavailable.
    return all.length ? new Set(all) : null
  }, [firstMonth.data, secondMonth.data])

  const availability = useGetAvailabilityQuery(
    {
      slug,
      branchId: branchId ?? 0,
      date,
      serviceIds,
      staffIds: hasPreference ? staffIds : undefined,
      sameStaff,
    },
    { skip: skipQueries },
  )

  const slots = useMemo(() => availability.data ?? [], [availability.data])
  const bookable = useMemo(() => slots.filter((s) => s.available), [slots])

  const grouped = useMemo(() => {
    const buckets = new Map<SlotBand, Slot[]>()
    for (const slot of slots) {
      const band = bandOf(slot.start)
      const bucket = buckets.get(band)
      if (bucket) bucket.push(slot)
      else buckets.set(band, [slot])
    }
    return buckets
  }, [slots])

  const chosen = selection.slotStart
  // A slot chosen before a cart change may no longer exist; the Continue button
  // must reflect what the server says now, not what the store remembers.
  const chosenIsLive = !!chosen && bookable.some((s) => s.start === chosen)

  useWizardAction(
    { label: 'Continue', onClick: () => navigate('../details'), disabled: !chosenIsLive },
    [chosenIsLive, navigate],
  )

  const pickDate = (key: string) => {
    setDate(key)
    if (chosen && slotDate(chosen) !== key) dispatch(setSlot(null))
  }

  return (
    <div className="container-page py-12 md:py-section-gap-md">
      <header className="mb-12 max-w-2xl">
        <h1 className="mb-4 font-display text-headline-lg-mobile text-primary md:text-headline-lg">
          Select date &amp; time
        </h1>
        <p className="font-body-lg text-body-lg text-on-surface-variant">
          Choose your preferred appointment slot. All times are shown in the salon&rsquo;s timezone (
          {bootstrap.timezone}).
        </p>
      </header>

      <section aria-label="Choose a date" className="mb-12">
        <div className="mb-6 flex items-center justify-between">
          <h2 className="font-display text-headline-sm text-on-surface">{monthShort(stripStart)}</h2>
          <div className="flex gap-2">
            <IconButton
              icon="chevron_left"
              label="Previous week"
              disabled={stripStart <= today}
              onClick={() => {
                const next = addDaysKey(stripStart, -7)
                setStripStart(next < today ? today : next)
              }}
            />
            <IconButton
              icon="chevron_right"
              label="Next week"
              onClick={() => setStripStart(addDaysKey(stripStart, 7))}
            />
          </div>
        </div>

        <div className="flex gap-3 overflow-x-auto pb-2 no-scrollbar">
          {strip.map((key) => {
            const closed = openDates ? !openDates.has(key) : false
            const active = key === date
            return (
              <button
                key={key}
                type="button"
                onClick={() => pickDate(key)}
                disabled={closed}
                aria-pressed={active}
                aria-label={formatLongDate(key)}
                title={closed ? `${formatLongDate(key)} — closed` : undefined}
                className={cn(
                  'flex h-24 w-[72px] shrink-0 flex-col items-center justify-center gap-2 rounded-xl border transition-colors duration-300',
                  active
                    ? 'border-primary bg-primary text-on-primary'
                    : 'border-outline-variant bg-surface-container-lowest text-on-surface hover:border-primary hover:text-primary',
                  closed &&
                    'cursor-not-allowed border-outline-variant/50 bg-surface-container-lowest text-outline-variant opacity-50 hover:border-outline-variant/50 hover:text-outline-variant',
                )}
              >
                <span className="font-label-caps text-label-caps uppercase">{weekdayShort(key)}</span>
                <span className="font-display text-headline-sm">{dayOfMonth(key)}</span>
              </button>
            )
          })}
        </div>
      </section>

      <section aria-label="Choose a time">
        <h2 className="mb-8 font-display text-headline-sm text-on-surface">{formatLongDate(date)}</h2>

        {availability.isLoading || availability.isFetching ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
            {Array.from({ length: 8 }, (_, i) => (
              <Skeleton key={i} shape="rect" className="h-12 w-full" />
            ))}
          </div>
        ) : availability.error ? (
          <InlineError error={availability.error} onRetry={availability.refetch} />
        ) : slots.length === 0 ? (
          <EmptyState
            icon="event_busy"
            title="No times on this day"
            body="We're either closed or fully booked. Pick another day from the strip above."
          />
        ) : bookable.length === 0 ? (
          <EmptyState
            icon="event_busy"
            title="This day is fully booked"
            body={
              hasPreference
                ? 'The specialist you picked is booked all day. Try another day, or go back and choose "Any specialist" for more times.'
                : 'Every time on this day is taken. Try another day.'
            }
          />
        ) : (
          <div className="flex flex-col gap-10">
            {BANDS.map((band) => {
              const items = grouped.get(band)
              if (!items?.length) return null
              return (
                <div key={band}>
                  <h3 className="mb-4 flex items-center gap-2 font-label-caps text-label-caps uppercase text-outline">
                    <span className="material-symbols-outlined text-[16px]">{BAND_ICON[band]}</span>
                    {BAND_LABEL[band]}
                  </h3>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
                    {items.map((slot) => {
                      const active = slot.start === chosen
                      return (
                        <button
                          key={slot.start}
                          type="button"
                          disabled={!slot.available}
                          onClick={() => dispatch(setSlot(slot.start))}
                          aria-pressed={active}
                          className={cn(
                            'rounded-lg px-4 py-3 text-center font-body-md text-body-md transition-colors duration-300',
                            slot.available
                              ? active
                                ? 'border-2 border-primary bg-primary/5 font-bold text-primary'
                                : 'border border-outline-variant text-on-surface hover:border-primary hover:text-primary'
                              : 'cursor-not-allowed border border-outline-variant bg-surface-container-lowest text-outline-variant opacity-50 line-through',
                          )}
                          title={slot.available ? undefined : 'This time has just been taken — pick another'}
                        >
                          {formatSlotTime(slot.start)}
                        </button>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </section>
    </div>
  )
}
